import React from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from 'v5/platform/dom/cn';
import { Sorter, SortOrder } from 'v5/api/_common/query/sorters/Sorter';
import { Nullable } from 'v5/platform/typescript';

export interface SortableTableHeaderProps<TField extends string> {
  /**
   * The field identifier for this column
   */
  field: TField;
  /**
   * The current sort state
   */
  sort: Nullable<Sorter<TField>>;
  /**
   * Callback when sort is toggled
   */
  onSortChange: (field: TField) => void;
  /**
   * The label to display
   */
  children: React.ReactNode;
  /**
   * Additional class names
   */
  className?: string;
  /**
   * Alignment of the header
   */
  align?: 'left' | 'center' | 'right';
}

/**
 * A sortable table header cell component that displays sort indicators
 * and handles sort toggling.
 *
 * Usage:
 * ```tsx
 * <SortableTableHeader
 *   field="title"
 *   sort={query.sort}
 *   onSortChange={handleSortChange}
 * >
 *   NAME
 * </SortableTableHeader>
 * ```
 */
export function SortableTableHeader<TField extends string>({
  field,
  sort,
  onSortChange,
  children,
  className,
  align = 'left'
}: SortableTableHeaderProps<TField>) {
  const isActive = sort?.field === field;
  const order: SortOrder | undefined = isActive ? sort?.order : undefined;

  const handleClick = () => {
    onSortChange(field);
  };

  const getSortIcon = () => {
    if (!isActive) {
      return (
        <ArrowUpDown className="shrink-0 w-4 h-4 text-fg-muted opacity-0 group-hover:opacity-100 transition-opacity" />
      );
    }
    if (order === 'asc') {
      return <ArrowUp className="shrink-0 w-4 h-4 text-fg-brand" />;
    }
    return <ArrowDown className="shrink-0 w-4 h-4 text-fg-brand" />;
  };

  const textAlignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  }[align];

  const justifyClass = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end'
  }[align];

  return (
    <th
      className={cn(
        'px-4 py-3 text-sm uppercase font-medium text-fg-muted bg-bg-default not-last:border-r border-border-default border-b',
        'cursor-pointer select-none group hover:bg-fg-default/3 transition-colors',
        textAlignClass,
        className
      )}
      onClick={handleClick}
    >
      <div className={cn('flex min-w-0 items-center gap-1.5', justifyClass)}>
        <span className="min-w-0 truncate">{children}</span>
        {getSortIcon()}
      </div>
    </th>
  );
}

/**
 * Hook to handle sort changes with the sorter utility
 */
export function useSortableTableHeader<TField extends string>(
  currentSort: Nullable<Sorter<TField>>,
  onSortChange: (sort: Nullable<Sorter<TField>>) => void
) {
  const handleSortChange = React.useCallback(
    (field: TField) => {
      // If clicking the same field, toggle between asc -> desc -> null
      if (currentSort?.field === field) {
        if (currentSort.order === 'asc') {
          onSortChange({ field, order: 'desc' });
        } else if (currentSort.order === 'desc') {
          onSortChange(null);
        }
      } else {
        // New field, start with asc
        onSortChange({ field, order: 'asc' });
      }
    },
    [currentSort, onSortChange]
  );

  return { handleSortChange };
}
